import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import {
    MdInventory, MdHistory, MdReportProblem, MdCheckCircle, MdAddBox,
    MdWarning, MdBuild, MdList
} from 'react-icons/md';

export default function CenterInventoryView({ user }) {
    const [subTab, setSubTab] = useState('mystock');
    const [myStock, setMyStock] = useState([]);
    const [myHistory, setMyHistory] = useState([]);
    const [myRequests, setMyRequests] = useState([]);
    const [loading, setLoading] = useState(false);

    // Forms
    const [requestForm, setRequestForm] = useState({ type: 'REPLACEMENT', subject: '', description: '', quantity_requested: 1, inventory_item_id: '' });
    const [actionForm, setActionForm] = useState({ selectedItems: [], remarks: '' });

    // Items List for Select (Fetched once)
    const [allItems, setAllItems] = useState([]);

    useEffect(() => {
        fetchMyStock();
        fetchItems();
    }, []);

    useEffect(() => {
        if (subTab === 'history') fetchHistory();
        if (subTab === 'requests') fetchRequests();
    }, [subTab]);

    const fetchMyStock = async () => {
        setLoading(true);
        try {
            const res = await api.get(`/inventory/center/${user.id}`);
            setMyStock(res.data);
        } catch (error) { console.error(error); } finally { setLoading(false); }
    };

    const fetchItems = async () => {
        try {
            const res = await api.get('/inventory'); // Generic item list
            setAllItems(res.data);
        } catch (error) { }
    };

    const fetchHistory = async () => {
        try {
            const res = await api.get(`/inventory/center-history/${user.id}`);
            setMyHistory(res.data);
        } catch (error) { }
    };

    const fetchRequests = async () => {
        try {
            const res = await api.get(`/inventory/requests?center_id=${user.id}`);
            setMyRequests(res.data);
        } catch (error) { }
    };


    const handleInstallMultiple = async () => {
        if (!actionForm.selectedItems || actionForm.selectedItems.length === 0) {
            alert("Please select at least one item.");
            return;
        }

        try {
            for (const stockId of actionForm.selectedItems) {
                const stockItem = myStock.find(s => s.id === stockId);
                if (stockItem && stockItem.quantity > 0) {
                    await api.post('/inventory/install', {
                        center_stock_id: stockId,
                        quantity: 1,
                        remarks: actionForm.remarks,
                        center_id: user.id
                    });
                }
            }
            alert(`Successfully installed ${actionForm.selectedItems.length} item(s)!`);
            fetchMyStock();
            setActionForm({ selectedItems: [], remarks: '' });
        } catch (error) {
            alert('Failed to update some items.');
        }
    };

    const handleReportFaultyMultiple = async () => {
        if (!actionForm.selectedItems || actionForm.selectedItems.length === 0) {
            alert("Please select at least one item.");
            return;
        }

        if (!actionForm.remarks.trim()) {
            alert("Please provide remarks/reason for marking as faulty.");
            return;
        }

        try {
            for (const stockId of actionForm.selectedItems) {
                const stockItem = myStock.find(s => s.id === stockId);
                if (stockItem && stockItem.installed_quantity > 0) {
                    await api.post('/inventory/faulty', {
                        center_stock_id: stockId,
                        quantity: 1,
                        source: 'installed',
                        remarks: actionForm.remarks,
                        center_id: user.id
                    });
                }
            }
            alert(`Successfully reported ${actionForm.selectedItems.length} item(s) as faulty!`);
            fetchMyStock();
            setActionForm({ selectedItems: [], remarks: '' });
        } catch (error) {
            alert(error.response?.data?.message || 'Failed');
        }
    };

    const handleSubmitRequest = async (e) => {
        e.preventDefault();
        try {
            await api.post('/inventory/requests', { ...requestForm, center_id: user.id });
            alert('Request Sent to Admin.');
            setRequestForm({ type: 'REPLACEMENT', subject: '', description: '', quantity_requested: 1, inventory_item_id: '' });
            fetchRequests();
        } catch (error) { alert('Failed'); }
    };

    return (
        <div className="fade-in">
            {/* Header */}
            <div className="civ-header" style={{
                background: 'linear-gradient(90deg, #2D3748 0%, #4A5568 100%)',
                padding: '1rem 1.5rem',
                borderRadius: '8px 8px 0 0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: 'white'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', marginBottom: '0.5rem' }}>
                    <MdInventory size={24} />
                    <div>
                        <h2 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>My Center Assets</h2>
                        <span style={{ fontSize: '0.8rem', opacity: 0.8 }}>Track received & installed hardware</span>
                    </div>
                </div>
                <div className="civ-header-tabs" style={{ display: 'flex', gap: '0.5rem' }}>
                    <TabButton label="My Stock" active={subTab === 'mystock'} onClick={() => setSubTab('mystock')} />
                    <TabButton label="History" active={subTab === 'history'} onClick={() => setSubTab('history')} />
                    <TabButton label="Requests" active={subTab === 'requests'} onClick={() => setSubTab('requests')} />
                </div>
            </div>

            <div style={{ background: 'white', padding: '1.5rem', borderRadius: '0 0 8px 8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)', minHeight: '400px' }}>

                {subTab === 'mystock' && (
                    <div className="civ-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: '2rem' }}>
                        <div className="civ-table-wrapper">
                            <h3 style={{ color: '#2D3748', borderBottom: '2px solid #E2E8F0', paddingBottom: '0.5rem', marginTop: 0 }}>Current Stock Status</h3>
                            {myStock.length === 0 ? <p style={{ color: '#718096' }}>No Assets Assigned yet.</p> : (
                                <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '600px' }}>
                                        <thead style={{ background: '#F7FAFC' }}>
                                            <tr>
                                                <th style={{ padding: '0.7rem 0.5rem', textAlign: 'left', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>Item Name</th>
                                                <th style={{ padding: '0.7rem 0.5rem', textAlign: 'left', fontSize: '0.85rem', color: '#4A5568', whiteSpace: 'nowrap' }}>Asset Tag</th>
                                                <th style={{ padding: '0.7rem 0.5rem', textAlign: 'center', fontSize: '0.85rem', color: '#2B6CB0', whiteSpace: 'nowrap' }}>In Store</th>
                                                <th style={{ padding: '0.7rem 0.5rem', textAlign: 'center', fontSize: '0.85rem', color: '#38A169', whiteSpace: 'nowrap' }}>Installed</th>
                                                <th style={{ padding: '0.7rem 0.5rem', textAlign: 'center', fontSize: '0.85rem', color: '#E53E3E', whiteSpace: 'nowrap' }}>Faulty</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {myStock.map(s => (
                                                <tr key={s.id} style={{ borderBottom: '1px solid #EDF2F7' }}>
                                                    <td style={{ padding: '0.7rem 0.5rem', fontWeight: '600', fontSize: '0.85rem' }}>{s.inventory_item?.name}</td>
                                                    <td style={{ padding: '0.7rem 0.5rem', fontFamily: 'monospace', fontWeight: 'bold', color: '#2C5282', fontSize: '0.8rem' }}>{s.asset_tag || '-'}</td>
                                                    <td style={{ padding: '0.7rem 0.5rem', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{s.quantity}</td>
                                                    <td style={{ padding: '0.7rem 0.5rem', textAlign: 'center', fontWeight: 'bold', fontSize: '0.9rem' }}>{s.installed_quantity}</td>
                                                    <td style={{ padding: '0.7rem 0.5rem', textAlign: 'center', fontWeight: 'bold', color: '#E53E3E', fontSize: '0.9rem' }}>{s.faulty_quantity}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        {/* Actions Sidebar */}
                        <div style={{ background: '#F7FAFC', padding: '1.5rem', borderRadius: '8px', border: '1px solid #E2E8F0', height: 'fit-content' }}>
                            <h4 style={{ margin: '0 0 1rem 0', color: '#2C5282' }}>Update Asset Status</h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                                <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #CBD5E0', borderRadius: '6px', padding: '0.5rem', background: 'white' }}>
                                    {myStock.length === 0 ? (
                                        <p style={{ color: '#718096', textAlign: 'center', padding: '1rem' }}>No items available</p>
                                    ) : (
                                        myStock.map(s => (
                                            <label key={s.id} style={{ display: 'flex', alignItems: 'center', padding: '0.6rem', borderBottom: '1px solid #EDF2F7', cursor: 'pointer' }} className="hover:bg-gray-50">
                                                <input
                                                    type="checkbox"
                                                    checked={actionForm.selectedItems?.includes(s.id) || false}
                                                    onChange={(e) => {
                                                        const selected = actionForm.selectedItems || [];
                                                        if (e.target.checked) {
                                                            setActionForm({ ...actionForm, selectedItems: [...selected, s.id] });
                                                        } else {
                                                            setActionForm({ ...actionForm, selectedItems: selected.filter(id => id !== s.id) });
                                                        }
                                                    }}
                                                    style={{ marginRight: '0.8rem', width: '18px', height: '18px', cursor: 'pointer' }}
                                                />
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontWeight: '600', color: '#2D3748', fontSize: '0.9rem' }}>{s.inventory_item?.name}</div>
                                                    <div style={{ fontSize: '0.75rem', color: '#718096' }}>
                                                        {s.asset_tag ? `Tag: ${s.asset_tag}` : 'Bulk'} • In Store: {s.quantity}
                                                    </div>
                                                </div>
                                            </label>
                                        ))
                                    )}
                                </div>

                                <input
                                    placeholder="Remarks (e.g. Lab 1, PC 5)"
                                    style={inputStyle}
                                    value={actionForm.remarks}
                                    onChange={e => setActionForm({ ...actionForm, remarks: e.target.value })}
                                />

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginTop: '0.5rem' }}>
                                    <button onClick={handleInstallMultiple} style={{ ...btnStyle, background: '#38A169' }}>
                                        <MdBuild /> Mark Installed
                                    </button>
                                    <button onClick={handleReportFaultyMultiple} style={{ ...btnStyle, background: '#E53E3E' }}>
                                        <MdWarning /> Mark Faulty
                                    </button>
                                </div>
                                <p style={{ fontSize: '0.75rem', color: '#718096', margin: '0.5rem 0' }}>
                                    * <b>Mark Installed:</b> Moves selected items from Store to In-Use.<br />
                                    * <b>Mark Faulty:</b> Moves selected from Installed to Faulty. Must return to Admin.
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {subTab === 'history' && (
                    <div>
                        <h3 style={{ margin: '0 0 1rem 0' }}>Transaction History</h3>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {myHistory.map(h => (
                                <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '1rem', borderBottom: '1px solid #EDF2F7', alignItems: 'center' }}>
                                    <div>
                                        <div style={{ fontWeight: '600', color: '#2D3748' }}>{h.inventory_item?.name} <span style={{ fontSize: '0.8rem', background: '#EDF2F7', padding: '2px 6px', borderRadius: '4px' }}>{h.transaction_type}</span></div>
                                        <div style={{ fontSize: '0.85rem', color: '#718096' }}>{h.remarks}</div>
                                    </div>
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontWeight: 'bold', fontSize: '1.1rem', color: h.transaction_type === 'DISTRIBUTE' ? '#2B6CB0' : '#4A5568' }}>
                                            {h.transaction_type === 'DISTRIBUTE' ? '+' : '-'}{h.quantity}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', color: '#A0AEC0' }}>{new Date(h.created_at).toLocaleString()}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {subTab === 'requests' && (
                    <div className="civ-grid civ-requests-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                        <div style={{ order: 2 }}>
                            <h3 style={{ margin: '0 0 1rem 0' }}>My Requests</h3>
                            {myRequests.map(r => (
                                <div key={r.id} style={{ padding: '1rem', border: '1px solid #E2E8F0', borderRadius: '8px', marginBottom: '0.8rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <span style={{ fontWeight: 'bold', color: '#2D3748' }}>{r.subject}</span>
                                        <span style={{ fontSize: '0.75rem', padding: '2px 8px', borderRadius: '10px', background: r.status === 'PENDING' ? '#FEFCBF' : '#C6F6D5', color: r.status === 'PENDING' ? '#B7791F' : '#22543D', fontWeight: 'bold' }}>{r.status}</span>
                                    </div>
                                    <div style={{ fontSize: '0.9rem', color: '#4A5568', marginTop: '0.4rem' }}>{r.description}</div>
                                    {r.admin_remarks && <div style={{ marginTop: '0.5rem', background: '#F0FFF4', padding: '0.5rem', borderRadius: '4px', fontSize: '0.85rem', color: '#276749' }}><b>Admin:</b> {r.admin_remarks}</div>}
                                </div>
                            ))}
                        </div>

                        <div style={{ background: '#FFF5F5', padding: '1.5rem', borderRadius: '8px', border: '1px solid #FEB2B2', height: 'fit-content', order: 1 }}>
                            <h4 style={{ margin: '0 0 1rem 0', color: '#C53030', display: 'flex', alignItems: 'center', gap: '0.5rem' }}><MdReportProblem size={20} /> New Request / Complaint</h4>
                            <form onSubmit={handleSubmitRequest} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                <select
                                    style={inputStyle}
                                    value={requestForm.type}
                                    onChange={e => setRequestForm({ ...requestForm, type: e.target.value })}
                                >
                                    <option value="NEW_REQUIREMENT">New Requirement</option>
                                    <option value="REPLACEMENT">Replacement (Faulty)</option>
                                    <option value="COMPLAINT">General Complaint</option>
                                </select>

                                <input
                                    placeholder="Subject (e.g. Need 5 Mouse)"
                                    style={inputStyle} required
                                    value={requestForm.subject}
                                    onChange={e => setRequestForm({ ...requestForm, subject: e.target.value })}
                                />

                                <textarea
                                    placeholder="Description / Reason"
                                    style={{ ...inputStyle, minHeight: '80px', fontFamily: 'inherit' }}
                                    value={requestForm.description}
                                    onChange={e => setRequestForm({ ...requestForm, description: e.target.value })}
                                />

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                                    <select
                                        style={inputStyle}
                                        value={requestForm.inventory_item_id}
                                        onChange={e => setRequestForm({ ...requestForm, inventory_item_id: e.target.value })}
                                    >
                                        <option value="">-- Related Item (Opt) --</option>
                                        {allItems.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                                    </select>
                                    <input
                                        type="number" min="1" placeholder="Qty"
                                        style={inputStyle}
                                        value={requestForm.quantity_requested}
                                        onChange={e => setRequestForm({ ...requestForm, quantity_requested: e.target.value })}
                                    />
                                </div>

                                <button type="submit" style={{ ...btnStyle, background: '#E53E3E' }}>Send Request</button>
                            </form>
                        </div>
                    </div>
                )}
            </div>
            <style>{`
                /* Mobile Optimizations */
                @media (max-width: 768px) {
                    .civ-header {
                        flex-direction: column;
                        align-items: stretch !important;
                        gap: 0.8rem;
                        padding: 1rem;
                    }
                    .civ-header-tabs {
                        width: 100%;
                        display: flex;
                        flex-wrap: nowrap; /* Force single line */
                        justify-content: space-between; /* Distribute evenly */
                        gap: 2px; /* Minimal gap */
                        padding-bottom: 0;
                        overflow: hidden; /* No scroll */
                    }
                    .civ-tab-btn {
                        font-size: 0.7rem !important; /* Small text to fit */
                        padding: 0.5rem 0.2rem !important; /* Minimal padding */
                        flex: 1; /* Grow to fill space */
                        white-space: normal !important; /* Allow wrapping inside button if REALLY needed, but prefer single line look */
                        line-height: 1.1;
                        text-align: center;
                        min-width: 0; /* Allow shrinking */
                    }
                    
                    .civ-grid {
                        display: flex !important;
                        flex-direction: column;
                    }
                    .civ-table-wrapper {
                        overflow-x: auto;
                    }
                }
            `}</style>
        </div >
    );
}

const TabButton = ({ label, active, onClick }) => (
    <button onClick={onClick} className="civ-tab-btn" style={{
        background: active ? 'rgba(255,255,255,0.2)' : 'transparent',
        border: 'none',
        color: 'white',
        padding: '0.6rem 1rem',
        borderRadius: '4px',
        cursor: 'pointer',
        fontWeight: '600',
        fontSize: '0.9rem',
        transition: 'background 0.2s'
    }}>
        {label}
    </button>
);

const inputStyle = { width: '100%', padding: '0.7rem', borderRadius: '6px', border: '1px solid #CBD5E0', boxSizing: 'border-box' };
const btnStyle = { padding: '0.5rem 0.4rem', fontSize: '0.8rem', whiteSpace: 'nowrap', minHeight: '36px', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.4rem' };
